/*
 * Theme behavior layer.
 *
 * The Airways Design System ships CSS only — all interactivity is owned by the
 * theme. JS hooks use `data-aero-*` attributes (never `air-*` classes, which are
 * styling-only). Keep this dependency-free vanilla JS.
 */
(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function decodeHTMLEntities(text) {
    var textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  function getYouTubeVideoId(url) {
    if (!url) return null;
    var regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    var match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  }

  function initMobileMenu() {
    var toggle = document.querySelector('[data-aero-menu-toggle]');
    var panel = document.querySelector('[data-aero-menu-panel]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', function () {
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
    });
  }

  function initPasswordRecovery() {
    var recover = document.querySelector('[data-aero-recover]');
    var login = document.querySelector('[data-aero-login-panel]');
    var toggle = document.querySelector('[data-aero-recover-toggle]');
    var cancel = document.querySelector('[data-aero-recover-cancel]');
    if (!recover || !toggle) return;

    function showRecover() {
      recover.hidden = false;
      if (login) login.hidden = true;
      var input = recover.querySelector('input[type="email"]');
      if (input) input.focus();
    }

    function hideRecover() {
      recover.hidden = true;
      if (login) login.hidden = false;
    }

    if (window.location.hash === '#recover') {
      showRecover();
    }

    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      showRecover();
    });

    if (cancel) {
      cancel.addEventListener('click', hideRecover);
    }
  }

  function initShareButtons() {
    document.querySelectorAll('[data-aero-share]').forEach(function (button) {
      if (!navigator.share) return;
      button.hidden = false;
      button.addEventListener('click', function () {
        navigator.share({
          url: button.getAttribute('data-share-url') || window.location.href,
          title: document.title
        });
      });
    });
  }

  function initBlogModal() {
    var modal = document.querySelector('[data-aero-blog-modal]');
    if (!modal) return;

    var videoWrapper = modal.querySelector('[data-aero-blog-modal-video]');
    var videoFrame = modal.querySelector('[data-aero-blog-modal-video-frame]');
    var modalBody = modal.querySelector('[data-aero-blog-modal-body]');

    function closeModal() {
      if (videoFrame) videoFrame.src = '';
      if (videoWrapper) videoWrapper.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
    }

    function openModal(trigger) {
      var title = trigger.getAttribute('data-title') || '';
      var content = trigger.getAttribute('data-content') || '';
      var videoUrl = trigger.getAttribute('data-video-url') || '';
      var decodedContent = decodeHTMLEntities(content);

      if (videoUrl && videoFrame && videoWrapper) {
        var videoId = getYouTubeVideoId(videoUrl);
        if (videoId) {
          videoFrame.src = 'https://www.youtube.com/embed/' + videoId;
          videoWrapper.hidden = false;
        }
      } else if (videoWrapper && videoFrame) {
        videoWrapper.hidden = true;
        videoFrame.src = '';
      }

      if (modalBody) {
        modalBody.innerHTML = '<h2>' + title + '</h2><div>' + decodedContent + '</div>';
      }

      modal.setAttribute('aria-hidden', 'false');
    }

    document.addEventListener('click', function (event) {
      var openTrigger = event.target.closest('[data-aero-blog-modal-open]');
      if (openTrigger) {
        event.preventDefault();
        openModal(openTrigger);
        return;
      }

      if (event.target.closest('[data-aero-blog-modal-close]')) {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.target === modal || event.target.closest('[data-aero-blog-modal-overlay]')) {
        closeModal();
      }
    });
  }

  function initVideoModals() {
    function closeVideoModal(modal) {
      if (!modal) return;
      var frame = modal.querySelector('[data-aero-video-modal-frame]');
      if (frame) frame.src = '';
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    document.addEventListener('click', function (event) {
      var openTrigger = event.target.closest('[data-aero-video-modal-open]');
      if (openTrigger) {
        event.preventDefault();
        var modal = document.querySelector(openTrigger.getAttribute('data-aero-video-modal-target') || '[data-aero-video-modal]');
        if (!modal) return;
        var frame = modal.querySelector('[data-aero-video-modal-frame]');
        var url = openTrigger.getAttribute('data-video-url') || '';
        if (frame && url) frame.src = url;
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        return;
      }

      if (event.target.closest('[data-aero-video-modal-close]') || event.target.closest('[data-aero-video-modal-overlay]')) {
        closeVideoModal(event.target.closest('[data-aero-video-modal]') || document.querySelector('[data-aero-video-modal][aria-hidden="false"]'));
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openModal = document.querySelector('[data-aero-video-modal][aria-hidden="false"]');
      if (openModal) closeVideoModal(openModal);
    });
  }

  /**
   * Add-to-cart is handled by assets/aero-component-cart-drawer.js.
   *
   * This used to bind a click listener on [data-aero-add-to-cart], preventDefault()
   * the PDP form's submit, POST /cart/add.js itself, and then hard-redirect to
   * /cart. That also suppressed the theme's own cart drawer, whose submit
   * listener never fired because the click handler cancelled the event first.
   * The drawer now owns the whole flow via a delegated submit listener on
   * [data-aero-cart-add-form].
   */

  function initCheckoutButtons() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-aero-checkout]');
      if (!button) return;
      event.preventDefault();
      var url = button.getAttribute('data-checkout-url') || button.getAttribute('href');
      if (url) window.location.href = url;
    });
  }

  /**
   * Wire up a product buy-box: size/pack selection, price, availability, Amazon
   * fallback and the thumbnail carousel.
   *
   * CONTAINER-SCOPED. Every lookup is rooted at `root` rather than `document`, and
   * the click listener is attached to `root`, so more than one instance can live on
   * a page without fighting. That is what lets the product quickview render the real
   * PDP section inside a <dialog> while an actual PDP is open behind it: each gets
   * its own instance and neither touches the other's DOM.
   *
   * @param {Element|Document} [root] - Container to scope to. Defaults to `document`.
   *   Callers should pass the `.shopify-section` element, not `document`, whenever
   *   two instances may coexist.
   * @param {Object} [options]
   * @param {boolean} [options.updateUrl=true] - Whether selecting a variant rewrites
   *   the `?variant=` URL param. MUST be false for the quickview: it is not the page
   *   the shopper is on, so writing its variant to the address bar would corrupt the
   *   URL and the back button.
   * @returns {{destroy: Function}|undefined} Teardown handle, or undefined when no
   *   product config was found in `root`.
   */
  function initProductPage(root, options) {
    root = root || document;
    options = options || {};
    var updateUrl = options.updateUrl !== false;

    var configEl = root.querySelector('[data-aero-product-config]');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    var selectedSize = null;
    var selectedPackSize = null;
    var variantSelect = root.querySelector('[data-aero-variant-select]');

    var PRICE_LEVEL = 'AH-RET-NA';

    function variantTitleFor(size, packSize) {
      return size + ' / ' + PRICE_LEVEL + ' / ' + (packSize || 'EA');
    }

    function findVariant(size, packSize) {
      var title = variantTitleFor(size, packSize);
      return config.variants.find(function (v) { return v.title === title; });
    }

    // ASIN is per-variant (a property of the SKU), so each size has its own listing.
    var amazonAsinByVariantId = config.amazonAsinByVariantId || {};
    var amazonDomain = config.amazonDomain || 'https://www.amazon.com/dp/';
    // Product-level default for sizes with no ASIN of their own — the same
    // `| default:` the two sections apply when they render the initial href.
    // config.amazonFallbackAvailable is still emitted but no longer read: the CTA is
    // never hidden now, so whether it exists at all is purely the Liquid's decision.
    var amazonFallbackAsin = config.amazonFallbackAsin || '';

    // Sync Add to Cart / Sold Out state and the Amazon CTA's href to a variant.
    // Availability comes straight from the product listing's inventory. A missing
    // variant is treated as sold out. Both buy paths are always offered; only the
    // cart controls change with stock.
    function updateAvailability(variant) {
      var available = Boolean(variant && variant.available);

      // querySelectorAll throughout: the quickview renders the buy box twice (mobile +
      // desktop), so updating only the first match would leave the visible copy stale.
      root.querySelectorAll('[data-aero-add-to-cart]').forEach(function (btn) {
        btn.disabled = !available;
        btn.textContent = available ? 'Add to Cart' : 'Sold Out';
      });

      root.querySelectorAll('[data-aero-quantity-selector]').forEach(function (el) {
        el.classList.toggle('air-display-none', !available);
      });

      // Point the CTA at the selected size's own listing, falling back to the product's
      // first listing when this size has no ASIN. The fallback is not cosmetic: the CTA is
      // always visible now, and the swap below only ran `if (asin)` — so a size without an
      // ASIN used to leave the PREVIOUS size's href in place. That stale link was invisible
      // while the CTA was hidden in exactly that case; it would be a mis-link today.
      var asin = (variant && amazonAsinByVariantId[variant.id]) || amazonFallbackAsin;
      if (asin) {
        root.querySelectorAll('[data-aero-amazon-link]').forEach(function (link) {
          link.href = amazonDomain + asin;
        });
      }

      // No CTA visibility toggle: Buy on Amazon is shown in every stock state (2026-09-03).
      // Stock is communicated by the Add to Cart button relabelling to a disabled Sold Out.
    }

    // Flag size swatches whose variant is missing or out of stock. Uses the DS
    // aria-disabled swatch state rather than a bespoke class.
    function updateSizeButtonStates() {
      root.querySelectorAll('[data-aero-select-size]').forEach(function (button) {
        var variant = findVariant(button.getAttribute('data-size'), selectedPackSize);
        var soldOut = !(variant && variant.available);
        button.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      });
    }

    function updateVariant() {
      if (!selectedSize || !variantSelect) return;

      var variant = findVariant(selectedSize, selectedPackSize);

      // Reflect availability on the buy buttons for the *selected* variant.
      updateAvailability(variant);

      if (!variant) return;

      // The quickview renders the buy box TWICE (mobile bar + desktop rail, one hidden
      // per breakpoint), so every buy-box element below must be updated with
      // querySelectorAll — a single querySelector would only ever reach the first copy,
      // which on desktop is the hidden mobile one. The PDP has a single copy, so the
      // loops simply run once there.
      root.querySelectorAll('[data-aero-variant-select]').forEach(function (input) {
        input.value = variant.id;
      });

      if (window.Shopify && Shopify.formatMoney) {
        root.querySelectorAll('.air-product__price').forEach(function (el) {
          el.textContent = Shopify.formatMoney(variant.price);
        });
      }

      root.querySelectorAll('.air-product__price--compare').forEach(function (el) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price && window.Shopify && Shopify.formatMoney) {
          el.textContent = Shopify.formatMoney(variant.compare_at_price);
          el.classList.remove('air-display-none');
        } else {
          el.classList.add('air-display-none');
        }
      });

      // Only the real PDP owns the address bar. A quickview is an overlay on some
      // other page (an article, the shop index), so writing its variant here would
      // rewrite that page's URL and break the back button.
      if (updateUrl) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    // Attached to `root`, not `document`: with two instances on the page, a click
    // inside the quickview must not reach the PDP's handler and vice versa.
    function onRootClick(event) {
      var sizeButton = event.target.closest('[data-aero-select-size]');
      if (sizeButton) {
        selectedSize = sizeButton.getAttribute('data-size');

        // Mark by VALUE, not by the clicked node: the quickview renders the size row
        // twice, so selecting only the clicked button would leave the hidden copy
        // showing a stale selection when the breakpoint changes.
        root.querySelectorAll('[data-aero-select-size]').forEach(function (btn) {
          var isSelected = btn.getAttribute('data-size') === selectedSize;
          btn.classList.toggle('air-btn--selected', isSelected);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
        updateVariant();
        return;
      }

      var packButton = event.target.closest('[data-aero-select-pack]');
      if (packButton) {
        root.querySelectorAll('[data-aero-select-pack].selected').forEach(function (btn) {
          btn.classList.remove('selected');
        });
        packButton.classList.add('selected');
        selectedPackSize = packButton.getAttribute('data-pack-size');
        updateSizeButtonStates();
        updateVariant();
        return;
      }

      var thumb = event.target.closest('[data-aero-carousel-thumb]');
      if (thumb) {
        // Scope the swap to the thumb's own gallery when there is one. The quickview
        // renders TWO galleries (mobile + desktop, one hidden per breakpoint), so a
        // root-level lookup would always update the FIRST — often the hidden copy.
        // The PDP has a single gallery and no wrapper, so it falls back to root.
        var gallery = thumb.closest('[data-aero-gallery]') || root;
        var mainImage = gallery.querySelector('[data-aero-product-main-image]');
        var src = thumb.getAttribute('data-image-src');
        if (mainImage && src) mainImage.src = src;

        // Reflect selection for assistive tech and the DS thumb styling.
        gallery.querySelectorAll('[data-aero-carousel-thumb]').forEach(function (t) {
          t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
        });
        return;
      }

      var carousel = root.querySelector('[data-aero-carousel-track]');
      if (!carousel) return;

      if (event.target.closest('[data-aero-carousel-prev]')) {
        if (carousel.scrollLeft <= 0) return;
        carousel.scrollBy({ left: -70, behavior: 'smooth' });
        setTimeout(updateCarouselButtons, 300);
      }

      if (event.target.closest('[data-aero-carousel-next]')) {
        if (carousel.scrollLeft + carousel.offsetWidth >= carousel.scrollWidth) return;
        carousel.scrollBy({ left: 70, behavior: 'smooth' });
        setTimeout(updateCarouselButtons, 300);
      }
    }

    root.addEventListener('click', onRootClick);

    function updateCarouselButtons() {
      var container = root.querySelector('[data-aero-carousel-track]');
      var prevButton = root.querySelector('[data-aero-carousel-prev]');
      var nextButton = root.querySelector('[data-aero-carousel-next]');
      if (!container || !prevButton || !nextButton) return;

      var threshold = 2;
      if (container.scrollLeft <= threshold) {
        prevButton.setAttribute('disabled', 'disabled');
        prevButton.classList.add('disabled');
      } else {
        prevButton.removeAttribute('disabled');
        prevButton.classList.remove('disabled');
      }

      if (container.scrollLeft + container.offsetWidth >= container.scrollWidth - threshold) {
        nextButton.setAttribute('disabled', 'disabled');
        nextButton.classList.add('disabled');
      } else {
        nextButton.removeAttribute('disabled');
        nextButton.classList.remove('disabled');
      }
    }

    var sizeButtons = root.querySelectorAll('[data-aero-select-size]');
    var packSizeButtons = root.querySelectorAll('[data-aero-select-pack]');

    if (sizeButtons.length > 0) {
      sizeButtons[0].classList.add('air-btn--selected');
      sizeButtons[0].setAttribute('aria-pressed', 'true');
      selectedSize = sizeButtons[0].getAttribute('data-size');
    }

    if (packSizeButtons.length > 0) {
      packSizeButtons[0].classList.add('selected');
      selectedPackSize = packSizeButtons[0].getAttribute('data-pack-size');
    }

    updateSizeButtonStates();

    if (selectedSize) updateVariant();

    var carousel = root.querySelector('[data-aero-carousel-track]');
    if (carousel) {
      carousel.addEventListener('scroll', updateCarouselButtons);
      updateCarouselButtons();
    }

    // Teardown so a re-opened quickview does not stack listeners on replaced DOM.
    return {
      destroy: function () {
        root.removeEventListener('click', onRootClick);
        if (carousel) carousel.removeEventListener('scroll', updateCarouselButtons);
      }
    };
  }

  // Public seam for the product quickview (assets/aero-extension-product-quickview.js),
  // which renders the real PDP section into a <dialog> and needs the same buy-box
  // behaviour scoped to that dialog. Mirrors window.AeroCartDrawer.
  if (typeof window !== 'undefined') {
    window.AeroProductPage = { init: initProductPage };
  }

  onReady(function () {
    initMobileMenu();
    initPasswordRecovery();
    initShareButtons();
    initBlogModal();
    initVideoModals();
    initCheckoutButtons();

    // Scope the PDP instance to its own section rather than `document`, so a
    // quickview opened on top of a PDP cannot reach these elements (and the PDP's
    // handler cannot reach the dialog's).
    var pdpConfig = document.querySelector('[data-aero-product-config]');
    if (pdpConfig) {
      initProductPage(pdpConfig.closest('.shopify-section') || document);
    }
  });
})();
